import type { Message, ProofState } from '../types/proof';
import { maskRocqComments } from './learningResources';

function goalData(raw: any): { foreground: any[]; unfocused: number } {
  const isGoal = (goal: any) => goal && typeof goal === 'object' && !Array.isArray(goal) &&
    !goal.nodeType && ('ty' in goal || 'ccl' in goal || 'type' in goal);
  const goalList = (value: any): any[] =>
    (Array.isArray(value) ? value : [value]).filter(isGoal);
  const count = (value: any): number =>
    Array.isArray(value) ? value.reduce((total, item) => total + count(item), 0) : Number(Boolean(isGoal(value)));
  if (Array.isArray(raw)) return { foreground: goalList(raw), unfocused: 0 };
  if (!raw || typeof raw !== 'object' || raw.nodeType) return { foreground: [], unfocused: 0 };
  return {
    // The foreground is all goals before a bullet, and just that branch after it.
    foreground: goalList(raw.fg_goals ?? raw.goals ?? raw),
    unfocused: count(raw.bg_goals ?? raw.stack) + count(raw.shelved_goals ?? raw.shelf) +
      count(raw.given_up_goals ?? raw.given_up),
  };
}

/** A null worker response is meaningful: there is no longer an open proof. */
export function rawGoalsAt(doc: { goals_raw?: Record<number, unknown> }, sid: number): unknown {
  return doc.goals_raw?.[sid];
}

/** JsCoq keeps query output on its sentence, including after moving focus back. */
export function sentenceMessages(sentence: { feedback?: unknown[] } | null | undefined, coqManager?: any): Message[] {
  const messages: Message[] = [];
  for (const feedback of sentence?.feedback ?? []) {
    if (!feedback || typeof feedback !== 'object') continue;
    const item = feedback as { level?: string | string[]; msg?: unknown; text?: unknown };
    const text = ppToString(item.msg ?? item.text, coqManager).trim();
    if (!text) continue;
    const rawLevel = String(Array.isArray(item.level) ? item.level[0] : item.level ?? 'info').toLowerCase();
    const level = rawLevel.includes('error') ? 'error' : rawLevel.includes('warn') ? 'warning' : 'info';
    // Deduplicate a worker replay within this sentence, never across commands.
    if (!messages.some(message => message.text === text && message.level === level)) messages.push({ level, text });
  }
  return messages;
}

export interface ProofStep {
  text: string;
  goals: unknown;
}

/** Rebuild from the current document prefix, so stepping back restores the count. */
export function proofProgress(steps: ProofStep[]): Pick<ProofState, 'subgoalProgress' | 'provedTheorem'> {
  let remaining: number | undefined;
  let progress: ProofState['subgoalProgress'];
  let theorem: string | undefined;
  let provedTheorem: string | undefined;
  for (const step of steps) {
    const command = maskRocqComments(step.text).trim();
    if (!command) continue;
    provedTheorem = undefined;
    const declaration = command.match(/^(?:Theorem|Lemma|Example|Corollary|Proposition|Fact|Remark)\s+([^\s:({]+)/u);
    if (declaration || /^Goal\b/.test(command) || /^(?:Definition|Fixpoint|CoFixpoint)\b/.test(command)) {
      theorem = declaration?.[1] ?? (/^Goal\b/.test(command) ? 'Unnamed theorem' : undefined);
      progress = undefined;
      remaining = undefined;
    }
    if (step.goals === undefined) continue;
    if (step.goals === null) {
      if (/^(?:Qed|Defined)\s*\./.test(command) && theorem) provedTheorem = theorem;
      if (!provedTheorem) progress = undefined;
      remaining = undefined;
      continue;
    }
    const data = goalData(step.goals);
    const nextRemaining = data.foreground.length + data.unfocused;
    const completed = (progress?.completed ?? 0) +
      (remaining === undefined ? 0 : Math.max(0, remaining - nextRemaining));
    progress = { completed, total: completed + nextRemaining };
    remaining = nextRemaining;
  }
  return { subgoalProgress: progress, provedTheorem };
}

function documentProgress(coqManager: any): ReturnType<typeof proofProgress> {
  const doc = coqManager?.doc;
  if (!doc?.sentences) return {};
  return proofProgress(doc.sentences
    .filter((sentence: any) => String(sentence.phase).toLowerCase() === 'processed' && !sentence.flags?.is_comment)
    .map((sentence: any) => ({ text: sentence.text, goals: rawGoalsAt(doc, sentence.coq_sid) })));
}

export function ppToString(pp: any, coqManager?: any): string {
  if (!pp) return '';
  if (typeof pp === 'string') return pp;

  if (coqManager?.pprint?.pp2DOM) {
    try {
      const $ = (window as any).$ || (globalThis as any).$;
      if ($) {
        const dom = coqManager.pprint.pp2DOM(pp);
        const text = dom.text() || dom.textContent || '';
        if (text.trim()) return text;
      }
    } catch (e) {
      // Fall through
    }
  }

  return extractStringFromPp(pp);
}

function extractStringFromPp(pp: any): string {
  if (typeof pp === 'string') return pp;
  if (!pp) return '';

  if (Array.isArray(pp)) {
    const tag = pp[0];

    if (tag === 'Pp_string' && pp.length >= 2) return pp[1];
    if (tag === 'Pp_glue' && pp.length >= 2 && Array.isArray(pp[1])) {
      return pp[1].map((elem: any) => extractStringFromPp(elem)).join('');
    }
    if (tag === 'Pp_box' && pp.length >= 3) return extractStringFromPp(pp[2]);
    if (tag === 'Pp_tag' && pp.length >= 3) return extractStringFromPp(pp[2]);
    if (tag === 'Pp_print_break' && pp.length >= 2) {
      return ' '.repeat(Math.max(0, pp[1] || 0));
    }
    if (tag === 'Pp_force_newline') return ' ';
    if (tag === 'Pp_empty') return '';

    const parts: string[] = [];
    for (let i = 0; i < pp.length; i++) {
      const part = extractStringFromPp(pp[i]);
      if (part) parts.push(part);
    }
    return parts.filter(p => p && typeof p === 'string' && !p.startsWith('Pp_')).join('');
  }

  return pp?.toString() || '';
}

function idToString(id: any, coqManager?: any): string {
  if (typeof id === 'string') return id;

  if (coqManager?.pprint) {
    const FormatPrettyPrint = (coqManager.pprint.constructor as any);
    if (FormatPrettyPrint?._idToString) {
      try {
        return FormatPrettyPrint._idToString(id);
      } catch (e) {
        // Fall through
      }
    }
  }

  if (Array.isArray(id)) {
    if (id.length >= 2 && typeof id[1] === 'string') return id[1];
    if (id.length >= 1) return id[0]?.toString() || '';
  }

  if (id && typeof id === 'object') {
    if (id.id) return id.id.toString();
    if (id.name) return id.name.toString();
    if (id.toString) return id.toString();
  }

  return id?.toString() || '';
}

export function parseJsCoqState(goals: any, coqManager: any, messagesData?: any[]): ProofState {
  try {
    const messages: Array<{ level: 'info' | 'warning' | 'error'; text: string }> = [];

    const { foreground: goalsArray, unfocused: unfocusedGoalCount } = goalData(goals);

    // Parse goals
    const parsedGoals = goalsArray.map((goal: any, idx: number) => {
      const goalHyps: Array<{ name: string; type: string }> = [];
      let goalType = '';

      if (goal && typeof goal === 'object' && !goal.nodeType) {
        // Raw goal structure
        const hypsArray = goal.hyp;

        if (hypsArray && Array.isArray(hypsArray)) {
          [...hypsArray].reverse().forEach((hyp: any) => {
            if (hyp && Array.isArray(hyp) && hyp.length >= 3) {
              const h_names = hyp[0];
              const h_def = hyp[1];
              const h_type = hyp[2];

              const hypType = Array.isArray(h_type) ? ppToString(h_type, coqManager) : (h_type?.toString() || '');

              if (Array.isArray(h_names) && h_names.length > 0 && hypType) {
                h_names.forEach((nameId: any) => {
                  const hypName = idToString(nameId, coqManager);

                  if (hypName && hypType) {
                    let fullType = hypType;
                    if (h_def) {
                      const defStr = Array.isArray(h_def) ? ppToString(h_def, coqManager) : h_def.toString();
                      if (defStr) {
                        fullType = `${hypName} := ${defStr} : ${hypType}`;
                      }
                    }

                    goalHyps.push({ name: hypName, type: fullType });
                  }
                });
              }
            }
          });
        }

        if (goal.ty !== undefined) {
          goalType = ppToString(goal.ty, coqManager);
        } else if (goal.ccl !== undefined) {
          goalType = ppToString(goal.ccl, coqManager);
        } else if (goal.type !== undefined) {
          goalType = Array.isArray(goal.type) ? ppToString(goal.type, coqManager) : goal.type.toString();
        }
      }

      return {
        id: `goal-${idx + 1}`,
        type: goalType,
        context: goalHyps,
        rawGoal: goal,
        goalCountText: undefined as string | undefined,
      };
    });

    // Add messages
    if (messagesData && Array.isArray(messagesData)) {
      messagesData.forEach((msg: any) => {
        if (msg?.text && msg.text.trim()) {
          messages.push({
            level: msg.level || 'info',
            text: msg.text.trim(),
          });
        }
      });
    }

    const goalCount = parsedGoals.length;
    const goalCountText = goalCount === 1 ? '1 goal' : `${goalCount} goals`;

    parsedGoals.forEach((goal) => {
      if (!goal.goalCountText) {
        goal.goalCountText = goalCountText;
      }
    });

    const progress = documentProgress(coqManager);
    return {
      goals: parsedGoals,
      hypotheses: parsedGoals[0]?.context ?? [],
      unfocusedGoalCount,
      messages,
      // Empty foreground goals can mean a finished branch or a proof awaiting Qed.
      isComplete: goals === null && (!coqManager?.doc || Boolean(progress.provedTheorem)),
      hasError: false,
      rawState: { goals: goalsArray },
      ...progress,
    };
  } catch (error) {
    console.error('Error parsing jsCoq state:', error);
    return {
      goals: [],
      hypotheses: [],
      messages: [{
        level: 'error',
        text: 'Failed to parse proof state',
      }],
      isComplete: false,
      hasError: true,
    };
  }
}
